
import { Game } from '../types';
import { fetchFootballAPI, getTeamLogo } from './footballService';

export interface TournamentTeam {
    id: string;
    name: string;
    logo: string;
    group: string;
    rank: 1 | 2;
    country?: string;
}

/**
 * 16강 대진표 자동 생성 알고리즘 (개선된 셔플 & 스왑 방식)
 * 
 * 규칙:
 * 1. 조 1위 팀(winners)과 조 2위 팀(runners-up)을 매칭합니다.
 * 2. 같은 조에 속했던 팀끼리는 맞붙을 수 없습니다.
 * 3. 같은 국가의 팀끼리는 맞붙을 수 없습니다 (16강 한정).
 */
export const generateR16Brackets = (groupWinners: TournamentTeam[], groupRunnersUp: TournamentTeam[]): { home: TournamentTeam, away: TournamentTeam }[] => {
    const winners = [...groupWinners];
    const runnersUp = [...groupRunnersUp];

    // 배열을 무작위로 섞는 유틸리티 함수
    const shuffle = (array: any[]) => {
        for (let i = array.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [array[i], array[j]] = [array[j], array[i]];
        }
        return array;
    };

    // 1위 팀과 2위 팀 모두 셔플하여 무작위성 극대화
    shuffle(winners);
    shuffle(runnersUp);

    const matchups: { home: TournamentTeam, away: TournamentTeam }[] = [];

    for (let i = 0; i < winners.length; i++) {
        const winner = winners[i];
        let runnerUp = runnersUp[i];

        // 규칙 검사 (같은 조 배제 & 같은 국가 배제)
        const isSameGroup = winner.group === runnerUp.group;
        const isSameCountry = winner.country && runnerUp.country && winner.country === runnerUp.country;

        if (isSameGroup || isSameCountry) {
            // 규칙 위반 시 다음 팀과 스왑 시도
            let swapped = false;
            for (let j = 1; j < runnersUp.length; j++) {
                const nextIdx = (i + j) % runnersUp.length;
                const potentialRunnerUp = runnersUp[nextIdx];
                
                // 스왑 대상 팀과의 규칙 검사
                const pSameGroup = winner.group === potentialRunnerUp.group;
                const pSameCountry = winner.country && potentialRunnerUp.country && winner.country === potentialRunnerUp.country;

                if (!pSameGroup && !pSameCountry) {
                    // 현재 runnerUp과 potentialRunnerUp을 교체
                    [runnersUp[i], runnersUp[nextIdx]] = [runnersUp[nextIdx], runnersUp[i]];
                    runnerUp = runnersUp[i];
                    swapped = true;
                    break;
                }
            }

            if (!swapped) {
                console.warn(`Could not find valid match for ${winner.name} after swapping.`);
            }
        }

        matchups.push({ home: winner, away: runnerUp });
    }

    return matchups;
};

/**
 * 8강 대진표 자동 생성 알고리즘
 * 
 * 규칙:
 * 1. 16강 승자(winners) 데이터를 기반으로 매칭합니다.
 * 2. 일반적으로 대진표의 순서에 따라 인접한 경기 승자끼리 매칭됩니다.
 */
export const generateQFBrackets = (r16Winners: TournamentTeam[]): { home: TournamentTeam, away: TournamentTeam }[] => {
    const matchups: { home: TournamentTeam, away: TournamentTeam }[] = [];
    
    // 8강 대진은 16강 대진의 순서대로 2팀씩 매칭
    for (let i = 0; i < r16Winners.length; i += 2) {
        if (r16Winners[i] && r16Winners[i + 1]) {
            matchups.push({ 
                home: r16Winners[i], 
                away: r16Winners[i + 1] 
            });
        }
    }
    
    return matchups;
};

/**
 * API로부터 조별리그 순위를 가져와서 TournamentTeam 형식으로 변환합니다.
 */
export const fetchTournamentTeams = async (leagueId: string, season: string): Promise<TournamentTeam[]> => {
    try {
        const res = await fetchFootballAPI(`/standings?league=${leagueId}&season=${season}`);
        if (!res?.response?.[0]?.league?.standings) return [];

        const allStandings = res.response[0].league.standings;
        const tournamentTeams: TournamentTeam[] = [];

        allStandings.forEach((groupStandings: any[]) => {
            // 각 조의 1, 2위 추출
            const topTwo = groupStandings.slice(0, 2);
            topTwo.forEach((s: any, index: number) => {
                tournamentTeams.push({
                    id: s.team.id.toString(),
                    name: s.team.name,
                    logo: getTeamLogo(s.team.id),
                    group: s.group, // 예: "Group A"
                    rank: (index + 1) as 1 | 2,
                    country: s.team.country || s.team.nation // API 응답 필드 대응
                });
            });
        });

        return tournamentTeams;
    } catch (e) {
        console.error("Fetch Tournament Teams Error:", e);
        return [];
    }
};
